import {CopilotAnimation, CopilotAnimationType} from '@github-ui/copilot-animation'
import type {CopilotChatMode} from '@github-ui/copilot-chat-types'
import {clsx} from 'clsx'
import {memo, useEffect, useState} from 'react'

import styles from './CopilotBadgeV2.module.css'

/**
 * The state of the badge, which determines the animation to show.
 * @param animationType - the current animation running or to run
 * @param onFinish - the animation to switch to after the current animation finishes
 * @param loopAnimation - whether or not to loop the incoming animation
 */
type BadgeState = {
  animationType: CopilotAnimationType
  onFinish: CopilotAnimationType
  loopAnimation: boolean
  hasLoadedMessage: boolean
}

const AFFIRM_KEYWORDS = [
  'yes',
  'absolutely',
  'certainly',
  'sure',
  'definitely',
  'right',
  'correct',
  'indeed',
  'precisely',
  'exactly',
  'true',
  'affirmative',
  'agree',
  'agreed',
]

const NEG_KEYWORDS = [
  'no',
  'never',
  'negative',
  'wrong',
  'false',
  'untrue',
  'cannot',
  "can't",
  'sorry',
  'incorrect',
  'deny',
  'disagree',
  'refuse',
  'unfortunately',
]

function isStrongAffirmative(message: string) {
  // Only check the first 200 characters of the message to reduce the chance of any
  // false positives from keywords that might appear in the middle of a long message.
  if (message.length < 200) {
    const tokens = message.toLowerCase().split(/\W+/).filter(Boolean)
    return AFFIRM_KEYWORDS.some(keyword => tokens.includes(keyword.toLowerCase()))
  }
  return false
}

function isStrongNegative(message: string) {
  if (message.length < 200) {
    const tokens = message.toLowerCase().split(/\W+/).filter(Boolean)
    return NEG_KEYWORDS.some(keyword => tokens.includes(keyword.toLowerCase()))
  }
  return false
}

interface GetNextAnimationState {
  previousState: BadgeState
  isThinking: boolean
  isAffirmativeSentiment: boolean
  isNegativeSentiment: boolean
  isError: boolean
  isLoading: boolean
  isFirstMessage: boolean
}

function getNextAnimationState({
  previousState,
  isThinking,
  isAffirmativeSentiment,
  isNegativeSentiment,
  isError,
  isLoading,
  isFirstMessage,
}: GetNextAnimationState): BadgeState {
  switch (previousState.animationType) {
    case CopilotAnimationType.Static: {
      // Positive/negative sentiment
      if ((isAffirmativeSentiment || isNegativeSentiment) && !isError && !previousState.hasLoadedMessage) {
        return {
          animationType: isAffirmativeSentiment ? CopilotAnimationType.Affirmative : CopilotAnimationType.Negative,
          onFinish: CopilotAnimationType.Static,
          loopAnimation: false,
          hasLoadedMessage: true,
        }
      }

      // Thinking
      if (isThinking) {
        return {
          ...previousState,
          animationType: CopilotAnimationType.Thinking,
          loopAnimation: true,
        }
      }
      // Confirm (only if not loading, not error, not has loaded)
      if (!isLoading && !previousState.hasLoadedMessage && !isError) {
        return {
          animationType: CopilotAnimationType.Confirm,
          onFinish: CopilotAnimationType.Static,
          loopAnimation: false,
          hasLoadedMessage: true,
        }
      }
      // Error -> Negative
      if (isError && !previousState.hasLoadedMessage) {
        return {
          animationType: CopilotAnimationType.Negative,
          onFinish: CopilotAnimationType.Static,
          loopAnimation: false,
          hasLoadedMessage: true,
        }
      }
      return previousState
    }

    case CopilotAnimationType.Thinking: {
      // Once Copilot is done thinking, celebrate the first reply or go negative if error.
      if (!isThinking) {
        if (isError) {
          return {
            ...previousState,
            animationType: CopilotAnimationType.Negative,
            loopAnimation: false,
            hasLoadedMessage: true,
          }
        }

        if (isFirstMessage && !previousState.hasLoadedMessage) {
          return {
            animationType: CopilotAnimationType.Celebrate,
            onFinish: CopilotAnimationType.Static,
            loopAnimation: false,
            hasLoadedMessage: true,
          }
        }

        return {
          ...previousState,
          animationType: CopilotAnimationType.Static,
          loopAnimation: false,
        }
      }
      return previousState
    }

    default:
      return previousState
  }
}

function isRecentMessage(createdAt: string): boolean {
  const msgDateTime = new Date(createdAt)
  const now = new Date()
  const minsDiff = (now.getTime() - msgDateTime.getTime()) / 1000 / 60

  return minsDiff < 0.5
}

interface CopilotBadgeProps {
  isLoading?: boolean
  isError?: boolean
  className?: string
  isThinking?: boolean
  isFirstMessage?: boolean
  mode: CopilotChatMode
  message: string
  createdAt: string
}
/**
 * Originally only utilized in immersive chat, this Copilot avatar does not have a border and has a "loading dots"
 * animation to indicate when Copilot is thinking. Currently called "V2" since the original CopilotBadge is still
 * utilized in several places.
 */
export function CopilotBadge({
  isFirstMessage = false,
  isLoading,
  isThinking,
  isError,
  className,
  mode,
  message,
  createdAt,
}: CopilotBadgeProps) {
  const [badgeState, setBadgeState] = useState<BadgeState>(() => {
    const startsThinking = Boolean(isThinking)
    const startsCelebrating = isFirstMessage && (!createdAt || isRecentMessage(createdAt))

    return {
      animationType: startsCelebrating
        ? CopilotAnimationType.Celebrate
        : startsThinking
          ? CopilotAnimationType.Thinking
          : CopilotAnimationType.Static,
      onFinish: CopilotAnimationType.Static,
      loopAnimation: startsThinking && !startsCelebrating,
      hasLoadedMessage: startsCelebrating,
    }
  })

  useEffect(() => {
    // Prevent old completed messages from reanimating when conversations reload, while
    // still letting active streaming placeholders animate before they have a timestamp.
    if (isLoading || isRecentMessage(createdAt)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBadgeState(previousState =>
        getNextAnimationState({
          previousState,
          isThinking: isThinking || false,
          isError: isError || false,
          isLoading: isLoading || false,
          isFirstMessage,
          isAffirmativeSentiment: isStrongAffirmative(message),
          isNegativeSentiment: isStrongNegative(message),
        }),
      )
    }
  }, [
    isThinking,
    isError,
    isLoading,
    badgeState.animationType,
    badgeState.hasLoadedMessage,
    isFirstMessage,
    message,
    createdAt,
  ])

  return (
    <div
      className={clsx(styles.copilotBadge, isLoading && styles.loading, className)}
      aria-label="Copilot badge"
      role="img"
    >
      <CopilotAnimation
        animationType={badgeState.animationType}
        onAnimationEnd={() => setBadgeState(prev => ({...prev, animationType: prev.onFinish}))}
        loopAnimation={badgeState.loopAnimation}
        className={clsx(styles.animation, mode === 'assistive' && styles.assistive)}
        size={mode === 'assistive' ? 28 : 32}
      />
    </div>
  )
}

export default memo(CopilotBadge)
